import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Download, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ReportsAdmin() {
  const [preview, setPreview] = useState(null);
  const [days, setDays] = useState(7);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/admin/reports/weekly/preview?days=${days}`).then((r) => setPreview(r.data)).catch(() => {});
  }, [days]);

  const download = async () => {
    const token = localStorage.getItem("fic_token");
    const r = await fetch(`${API_BASE}/admin/reports/weekly/download?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fic_weekly_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  const sendNow = async () => {
    if (!preview?.email_configured) {
      toast.error("Add RESEND_API_KEY to backend .env first");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/reports/weekly/send?days=${days}`);
      toast.success(`Sent to ${r.data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-reports-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Insights</div>
        <h1 className="font-serif text-4xl mt-2 text-ink">Weekly Report</h1>
        <p className="mt-2 text-ink/60">Download or email a multi-sheet summary of subscribers, contact messages, and donations.</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 items-center">
        <label className="text-xs uppercase tracking-wider text-ink/60">Period</label>
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`period-${d}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all border ${
              days === d ? "bg-navy text-cream border-navy" : "bg-white border-sand text-ink/70 hover:border-navy/40"
            }`}
          >
            Last {d} days
          </button>
        ))}
      </div>

      {preview && (
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Stat label="New subscribers" value={preview.subscribers} />
          <Stat label="Contact messages" value={preview.messages} />
          <Stat label="Donations (count)" value={preview.donations_count} />
          <Stat label="Donations (total)" value={`$${preview.donations_total.toFixed(2)}`} />
        </div>
      )}

      <div className="rounded-3xl bg-white border border-sand p-8 flex flex-wrap gap-4 items-center">
        <button
          onClick={download}
          data-testid="download-report-btn"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-semibold hover:bg-navy-900"
        >
          <Download size={14} /> Download XLSX
        </button>
        <button
          onClick={sendNow}
          disabled={sending}
          data-testid="send-report-btn"
          className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-6 py-3 text-sm font-semibold hover:bg-gold-600 disabled:opacity-60"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email now to {preview?.recipient || "admin"}
        </button>
        {preview && !preview.email_configured && (
          <div className="text-xs text-ink/60 flex-1 min-w-[200px]">
            ℹ Email service is <strong>not configured</strong>. Add <code className="bg-sand/60 px-1.5 py-0.5 rounded">RESEND_API_KEY</code> in
            backend <code>.env</code> (free tier at resend.com) and restart backend to enable "Email now".
          </div>
        )}
      </div>

      <div className="mt-8 rounded-3xl bg-sand/50 border border-sand p-6 text-sm text-ink/70 leading-relaxed">
        <strong className="text-ink">What's in the report:</strong> A 4-sheet Excel workbook — (1) Summary,
        (2) Newsletter subscribers in period, (3) Contact messages in period, (4) Donations in period. You can open it in Excel,
        Google Sheets, or Numbers.
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-sand p-6">
      <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{label}</div>
      <div className="font-serif text-3xl mt-2 text-ink">{value}</div>
    </div>
  );
}
